/**
 * CABFileList — Displays uploaded CAB files with metadata and actions.
 */

import React from 'react';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CABFile, CABFileCategory, FileUploadState } from '../../types/cab';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';

interface CABFileListProps {
  files: CABFile[];
  uploadingFiles?: FileUploadState[];
  onDeleteFile: (fileId: string) => void;
  onDownloadFile?: (fileId: string, fileName: string) => void;
  disabled?: boolean;
  permissions?: string[];
}

/** Map file category to an icon. */
function getFileIcon(category: CABFileCategory): IconName {
  switch (category) {
    case 'document':
      return IconName.Document;
    case 'source_code':
      return IconName.Code;
    case 'archive':
      return IconName.Folder;
    case 'image':
      return IconName.Preview;
    default:
      return IconName.File;
  }
}

/** Format bytes to human-readable. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Format file category for display. */
function formatCategory(category: CABFileCategory): string {
  switch (category) {
    case 'document': return 'Document';
    case 'source_code': return 'Source Code';
    case 'archive': return 'Archive';
    case 'image': return 'Image';
    default: return 'File';
  }
}

/** Get file extension from filename */
function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/** Guess file category from extension for uploading files */
function guessFileCategory(filename: string): CABFileCategory {
  const ext = getFileExtension(filename);
  
  const documentExts = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv', 'txt', 'md'];
  const codeExts = ['py', 'js', 'ts', 'jsx', 'tsx', 'java', 'cs', 'cpp', 'c', 'go', 'rs', 'rb', 'php', 'swift', 'kt', 'json', 'xml', 'yaml', 'yml', 'sql', 'css', 'scss', 'html', 'vue', 'svelte'];
  const archiveExts = ['zip', 'tar', 'gz', 'rar', '7z'];
  const imageExts = ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp'];
  
  if (documentExts.includes(ext)) return 'document';
  if (codeExts.includes(ext)) return 'source_code';
  if (archiveExts.includes(ext)) return 'archive';
  if (imageExts.includes(ext)) return 'image';
  return 'unknown';
}

export const CABFileList: React.FC<CABFileListProps> = ({
  files,
  uploadingFiles = [],
  onDeleteFile,
  onDownloadFile,
  disabled = false,
  permissions = [],
}) => {
  const totalFiles = files.length + uploadingFiles.length;
  
  if (totalFiles === 0) return null;

  return (
    <div className="mt-bg-v2-md">
      <div className="flex justify-between items-center mb-bg-v2-sm">
        <Text
          variant="text-xs"
          weight="semibold"
          className="text-text-primary uppercase tracking-wider"
        >
          {uploadingFiles.length > 0 
            ? `Files (${files.length} uploaded, ${uploadingFiles.length} uploading)`
            : `Uploaded Files (${files.length})`
          }
        </Text>
      </div>

      <div className="flex flex-col gap-bg-v2-xs">
        {/* Show uploading files first */}
        {uploadingFiles.map((uploadState) => (
          <div
            key={uploadState.tempId}
            className="
              flex items-center gap-bg-v2-sm
              p-bg-v2-sm px-bg-v2-md
              bg-bg-secondary rounded-bg-v2-md
              border border-transparent
              transition-all duration-200
            "
          >
            {/* File icon */}
            <div className="w-8 h-8 bg-bg-brand-primary rounded-bg-v2-sm flex items-center justify-center shrink-0">
              <Icon
                name={getFileIcon(guessFileCategory(uploadState.file.name))}
                size="sm"
                className="!text-text-brand-primary"
                inheritColor
              />
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <Text
                variant="text-xs"
                weight="medium"
                className="text-text-primary truncate block"
              >
                {uploadState.file.name}
              </Text>
              <Text variant="text-xs" className="text-text-tertiary">
                {formatSize(uploadState.file.size)} &middot; {formatCategory(guessFileCategory(uploadState.file.name))}
              </Text>
            </div>

            {/* Upload status */}
            <div className="shrink-0 flex items-center gap-bg-v2-sm">
              {uploadState.status === 'uploading' && (
                <>
                  <div className="w-16">
                    <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-bg-brand-solid rounded-full transition-all duration-300"
                        style={{ width: `${uploadState.progress}%` }}
                      />
                    </div>
                  </div>
                  <div className="w-4 h-4 border-2 border-border-secondary border-t-bg-brand-solid rounded-full animate-spin" />
                </>
              )}
              {uploadState.status === 'pending' && (
                <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full text-bg-v2-xs font-bg-v2-medium bg-bg-tertiary text-text-tertiary">
                  Pending...
                </span>
              )}
              {uploadState.status === 'completed' && (
                <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full text-bg-v2-xs font-bg-v2-medium bg-bg-success-primary text-text-success-primary">
                  ✓ Uploaded
                </span>
              )}
              {uploadState.status === 'error' && (
                <span className="inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full text-bg-v2-xs font-bg-v2-medium bg-bg-error-primary text-text-error-primary" title={uploadState.error}>
                  ✗ Failed
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Show uploaded files */}
        {files.map((file) => (
          <div
            key={file.file_id}
            className="
              flex items-center gap-bg-v2-sm
              p-bg-v2-sm px-bg-v2-md
              bg-bg-secondary rounded-bg-v2-md
              border border-transparent
              transition-all duration-200
              hover:bg-bg-tertiary hover:border-border-secondary
            "
          >
            {/* File icon */}
            <div className="w-8 h-8 bg-bg-brand-primary rounded-bg-v2-sm flex items-center justify-center shrink-0">
              <Icon
                name={getFileIcon(file.file_category)}
                size="sm"
                className="!text-text-brand-primary"
                inheritColor
              />
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <Text
                variant="text-xs"
                weight="medium"
                className="text-text-primary truncate block"
              >
                {file.original_name}
              </Text>
              <Text variant="text-xs" className="text-text-tertiary">
                {formatSize(file.file_size)} &middot; {formatCategory(file.file_category)}
              </Text>
            </div>

            {/* Upload status badge */}
            <div className="shrink-0">
              <span
                className={`
                  inline-block px-bg-v2-sm py-bg-v2-xxs rounded-full text-bg-v2-xs font-bg-v2-medium
                  ${file.upload_status === 'completed'
                    ? 'bg-bg-success-primary text-text-success-primary'
                    : file.upload_status === 'uploading'
                      ? 'bg-bg-warning-primary text-text-warning-primary'
                      : 'bg-bg-error-primary text-text-error-primary'}
                `}
              >
                {file.upload_status === 'completed' ? 'Uploaded'
                  : file.upload_status === 'uploading' ? 'Uploading...'
                  : 'Error'}
              </span>
            </div>

            {/* Action buttons */}
            {disabled ? (
              <button
                type="button"
                onClick={() => onDownloadFile?.(file.file_id, file.original_name)}
                className="
                  p-bg-v2-xs rounded-bg-v2-sm
                  text-text-tertiary hover:text-text-brand-primary hover:bg-bg-brand-primary
                  transition-all duration-200
                "
                title="Download file"
              >
                <Icon name={IconName.Download} size="sm" inheritColor />
              </button>
            ) : (
              <IfHasPermission
                permissions={permissions}
                requires={Permissions.UpdateProjectPermission}
              >
                <button
                  type="button"
                  onClick={() => onDeleteFile(file.file_id)}
                  className="
                    p-bg-v2-xs rounded-bg-v2-sm
                    text-text-tertiary hover:text-text-primary hover:bg-bg-tertiary
                    transition-all duration-200
                  "
                  title="Remove file"
                >
                  <Icon name={IconName.XClose} size="sm" inheritColor />
                </button>
              </IfHasPermission>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
